/**
 * Stage-mapping service for SubContractorHub (Story SCH-2.6).
 *
 * Wraps the four Stage-Mapping CRUD endpoints + the default-landing-stage
 * endpoint + the two SCH-2.6 vocabulary endpoints (POST /discover-stage-values
 * and GET /stage-vocabulary). Mirrors fieldMappingService.ts conventions:
 * shared fetchJson helper, snake_case ↔ camelCase translation at the
 * boundary, discriminated union result types so callers don't have to throw.
 *
 * SCH-2.6 supersedes SCH-2.2's milestone-field-key precedence model. The
 * `reorderStageMappings` method has been removed — 1:1 lookup has no order.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  CreateStageMappingRequest,
  CreateStageMappingResult,
  DeleteStageMappingResult,
  ListStageMappingsResult,
  SetDefaultLandingStageRequest,
  SetDefaultLandingStageResult,
  StageMappingRow,
  StageVocabulary,
  StageVocabularyResult,
  UpdateStageMappingRequest,
  UpdateStageMappingResult,
} from '@/types/integrations/stageMapping';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchJson<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

// ─── Raw → camelCase row mapper ──────────────────────────────────────────
interface RawStageMappingRow {
  id: string;
  tenant_integration_id: string;
  external_stage_value: string;
  joules_stage_id: number;
  joules_stage_name: string | null;
  joules_pipeline_id: number | null;
  joules_pipeline_name: string | null;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

function _mapStageRow(raw: RawStageMappingRow): StageMappingRow {
  return {
    id: raw.id,
    tenantIntegrationId: raw.tenant_integration_id,
    externalStageValue: raw.external_stage_value,
    joulesStageId: raw.joules_stage_id,
    joulesStageName: raw.joules_stage_name,
    joulesPipelineId: raw.joules_pipeline_id ?? null,
    joulesPipelineName: raw.joules_pipeline_name ?? null,
    enabled: raw.enabled,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawStageVocabulary {
  stages: string[];
  discovered_at: string | null;
  projects_sampled: number;
  pages_sampled: number;
  null_stage_count: number;
}

function _mapVocabulary(raw: RawStageVocabulary): StageVocabulary {
  return {
    stages: raw.stages ?? [],
    discoveredAt: raw.discovered_at,
    projectsSampled: raw.projects_sampled ?? 0,
    pagesSampled: raw.pages_sampled ?? 0,
    nullStageCount: raw.null_stage_count ?? 0,
  };
}

const STAGE_ERROR_CODES = ['integration_not_found', 'internal_error'] as const;

const VOCAB_ERROR_CODES = [
  'integration_not_found',
  'auth_failed',
  'connection_test_failed',
  'internal_error',
] as const;

function _normalizeError<TCode extends string>(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
  knownCodes: readonly TCode[],
): { code: TCode; message: string; details?: Record<string, unknown> } {
  if (!raw) {
    return { code: 'internal_error' as TCode, message: 'Unknown error' };
  }
  const code = (knownCodes as readonly string[]).includes(raw.code)
    ? (raw.code as TCode)
    : ('internal_error' as TCode);
  return { code, message: raw.message, details: raw.details };
}

export const stageMappingService = {
  // ─── GET /stage-mappings ────────────────────────────────────────────────
  async listStageMappings(): Promise<ListStageMappingsResult> {
    const env = await fetchJson<{ rows: RawStageMappingRow[] }>(
      '/api/v1/integrations/subcontractorhub/stage-mappings',
      { method: 'GET' },
    );
    if (env.data) {
      return { data: { rows: env.data.rows.map(_mapStageRow) }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, STAGE_ERROR_CODES) };
  },

  // ─── POST /stage-mappings (UPSERT) ──────────────────────────────────────
  async createStageMapping(
    req: CreateStageMappingRequest,
  ): Promise<CreateStageMappingResult> {
    const body: Record<string, unknown> = {
      external_stage_value: req.externalStageValue,
      joules_stage_id: req.joulesStageId,
    };
    if (req.enabled !== undefined) body.enabled = req.enabled;

    const env = await fetchJson<RawStageMappingRow>(
      '/api/v1/integrations/subcontractorhub/stage-mappings',
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (env.data) {
      return { data: _mapStageRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, STAGE_ERROR_CODES) };
  },

  // SCH-2.6: reorderStageMappings REMOVED. 1:1 lookup has no order.

  // ─── PATCH /stage-mappings/{id} ─────────────────────────────────────────
  async updateStageMapping(
    id: string,
    req: UpdateStageMappingRequest,
  ): Promise<UpdateStageMappingResult> {
    const body: Record<string, unknown> = {};
    if (req.joulesStageId !== undefined) body.joules_stage_id = req.joulesStageId;
    if (req.enabled !== undefined) body.enabled = req.enabled;

    const env = await fetchJson<RawStageMappingRow>(
      `/api/v1/integrations/subcontractorhub/stage-mappings/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    if (env.data) {
      return { data: _mapStageRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, STAGE_ERROR_CODES) };
  },

  // ─── DELETE /stage-mappings/{id} ────────────────────────────────────────
  async deleteStageMapping(id: string): Promise<DeleteStageMappingResult> {
    const env = await fetchJson<{ deleted_id: string }>(
      `/api/v1/integrations/subcontractorhub/stage-mappings/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (env.data) {
      return { data: { deletedId: env.data.deleted_id }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, STAGE_ERROR_CODES) };
  },

  // ─── PATCH /default-landing-stage ───────────────────────────────────────
  async setDefaultLandingStage(
    req: SetDefaultLandingStageRequest,
  ): Promise<SetDefaultLandingStageResult> {
    const env = await fetchJson<{
      default_landing_stage_id: number | null;
      default_landing_stage_name: string | null;
    }>(
      '/api/v1/integrations/subcontractorhub/default-landing-stage',
      {
        method: 'PATCH',
        body: JSON.stringify({ joules_stage_id: req.joulesStageId }),
      },
    );
    if (env.data) {
      return {
        data: {
          defaultLandingStageId: env.data.default_landing_stage_id,
          defaultLandingStageName: env.data.default_landing_stage_name,
        },
        error: null,
      };
    }
    return { data: null, error: _normalizeError(env.error, STAGE_ERROR_CODES) };
  },

  // ─── POST /discover-stage-values (SCH-2.6) ──────────────────────────────
  async discoverStageValues(): Promise<StageVocabularyResult> {
    const env = await fetchJson<RawStageVocabulary>(
      '/api/v1/integrations/subcontractorhub/discover-stage-values',
      { method: 'POST' },
    );
    if (env.data) {
      return { data: _mapVocabulary(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, VOCAB_ERROR_CODES) };
  },

  // ─── GET /stage-vocabulary (SCH-2.6) ────────────────────────────────────
  async getStageVocabulary(): Promise<StageVocabularyResult> {
    const env = await fetchJson<RawStageVocabulary>(
      '/api/v1/integrations/subcontractorhub/stage-vocabulary',
      { method: 'GET' },
    );
    if (env.data) {
      return { data: _mapVocabulary(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, VOCAB_ERROR_CODES) };
  },
};
